/**
 * Main Component for our Analytics Graphs that loads
 * all different Graph types. Also includes a Feature-
 * Gate for this feature.
 *
 * @component
 */

import { Area } from '@ant-design/charts';
import {
  Button,
  Col,
  DatePicker,
  Divider,
  message,
  Row,
  Spin,
  Typography,
  Menu,
  Dropdown,
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import axios from 'axios';
import { Parser } from 'json2csv';
import dayjs from 'dayjs';
import React from 'react';
import { CSVLink } from 'react-csv';
import Upgrade from '../../../../components/Upgrade';
import { SubscriptionContext } from '../../../../contexts/SubscriptionContext';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const { RangePicker } = DatePicker;

const { Text } = Typography;

// testing visualization color
const config = {
  areaStyle: function areaStyle() {
    return { fill: 'l(270) 0:#ffffff 0.5:#7ec2f3 1:#1890ff' };
  },
};

class VolumeGraph extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      exportLoading: false,
      date: undefined,
      data: [],
      dataCSVToBeExported: [],
      pdfLoading: false,
      timestamp: Date.now(),
    };
    this.printRef = React.createRef();
    this.csvLinkRef = React.createRef();
  }
  static contextType = SubscriptionContext;

  componentDidMount() {
    this.handleSubmit();
  }

  handleExportPdf = async () => {
    this.setState({ pdfLoading: true });

    try {
      if (this.props.startDate == null || this.props.endDate == null) {
        this.setState({ pdfLoading: false });
        return message.error('Select a start and end date first.', 4);
      }

      if (this.state.data.filter((item) => item.volume !== 0).length === 0) {
        this.setState({ pdfLoading: false });
        return message.error(
          'There is no data to export for the selected date range.',
          4
        );
      }

      const element = this.printRef.current;
      if (!element) {
        this.setState({ pdfLoading: false });
        return message.error('No content to export as PDF');
      }
      const originalStyle = element.style.cssText;
      element.style.cssText = `
        ${originalStyle} 
        padding: 10px;
      `;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
      });
      element.style.cssText = originalStyle;
      const data = canvas.toDataURL('image/png');

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: 'a4',
      });

      const imgProperties = pdf.getImageProperties(data);
      const pdfWidth = pdf.internal.pageSize.getWidth() - 10;
      const pdfHeight = (imgProperties.height * pdfWidth) / imgProperties.width;

      pdf.addImage(data, 'PNG', 0, 20, pdfWidth, pdfHeight);
      pdf.save(
        `RichReturns-Trend-${this.props.tab}-${this.state.timestamp}.pdf`
      );

      message.success('PDF downloaded successfully');
    } catch (error) {
      this.setState({
        pdfLoading: false,
      });
      return message.error('Failed to generate PDF');
    } finally {
      this.setState({ pdfLoading: false });
    }
  };

  async handleExportCsv() {
    this.setState({
      exportLoading: true,
    });

    if (this.props.startDate == null || this.props.endDate == null) {
      this.setState({
        exportLoading: false,
      });
      return message.error('Select a start and end date first.', 4);
    }

    try {
      const response = await axios.post(this.props.url, {
        startDate: this.props.startDate,
        endDate: this.props.endDate,
        isExport: true,
      });

      if (response.data.error) {
        this.setState({
          exportLoading: false,
        });
        return message.error('Error creating export file of the Trend.', 4);
      }

      if (response.data.emailExport) {
        this.setState({
          exportLoading: false,
        });
        return message.success(
          'Trend is being generated. You will receive an email shortly with the download link.',
          4
        );
      }

      let dataParser = new Parser({
        fields: this.props.exportHeaders,
        quote: '',
      });
      response.data.data = response.data.data.filter(
        (item) => item.volume !== 0
      );

      if (response.data.data.length === 0) {
        this.setState({
          exportLoading: false,
        });
        return message.error(
          'There is no data to export for the selected date range.',
          4
        );
      }
      const dataCSVToBeExported = dataParser.parse(response.data.data);

      this.setState(
        {
          exportLoading: false,
          dataCSVToBeExported,
        },
        () => {
          if (
            this.csvLinkRef.current &&
            this.state.dataCSVToBeExported.length > 0
          ) {
            this.csvLinkRef.current.link.click();
            message.success('CSV downloaded successfully!', 2);
            this.handleResetDataToBeExported();
          }
        }
      );
    } catch (err) {
      this.setState({
        exportLoading: false,
      });
      return message.error('Error creating report csv.', err);
    }
  }

  async handleSubmit() {
    if (this.context && this.context.featureAnalyticsAdvanced === false) {
      return;
    }

    this.setState({
      loading: true,
    });

    if (!this.props.startDate || !this.props.endDate) {
      this.setState({
        loading: false,
      });
      return;
    }
    try {
      const response = await axios.post(this.props.url, {
        startDate: this.props.startDate,
        endDate: this.props.endDate,
      });

      if (response.data.error) {
        return message.error("Error fetching graph's data from server.", 4);
      }

      return this.setState({
        data: response.data.data,
        loading: false,
      });
    } catch (err) {
      message.error("Error fetching graph's data.", 4);
    }
  }

  onChange(dates, dateStrings) {
    this.props.updateDate(dateStrings);
    this.setState(
      {
        checkboxDisabled: false,
      },
      () => {
        this.handleSubmit();
      }
    );
  }
  handleResetDataToBeExported = () => {
    this.setState({
      dataCSVToBeExported: [],
    });
  };
  render() {
    const { date } = this.state;

    const disabledDate = (current) => {
      if (!date) {
        return false;
      }
      const tooLate = date && current.diff(date, 'days') > 366;
      const tooEarly = date && date.diff(current, 'days') > 45;
      return tooEarly || tooLate;
    };

    return (
      <React.Fragment>
        {this.context.featureAnalyticsAdvanced === null ? (
          <Spin
            spinning={true}
            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
          />
        ) : (
          <React.Fragment>
            {/* 
                <Row
                  type="flex"
                  justify="start"
                  align="top"
                >
                  <Col>
                    <h1 className="TextHeading TextHeading--sm">
                      <Tooltip title={this.props.tooltip}>
                        <Icon
                          style={{ fontSize: "16px", alignItems: "center" }}
                          type="info-circle"
                        />
                      </Tooltip>{" "}
                      {this.props.title}
                    </h1> 
                  </Col>

                  <Col>
                  </Col>
                </Row>
                */}
            {this.context.featureAnalyticsAdvanced ? (
              <React.Fragment>
                <Row
                  type="flex"
                  justify="space-between"
                  align="middle"
                  style={{ marginBottom: '20px' }}
                  gutter={16}
                >
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 8 }}
                    md={{ span: 10 }}
                    lg={{ span: 12 }}
                  >
                    <Text style={{ fontSize: 14 }}>{this.props.tooltip}</Text>
                  </Col>

                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 10 }}
                    md={{ span: 8 }}
                    lg={{ span: 8 }}
                  >
                    <Spin spinning={this.state.loading}>
                      <RangePicker
                        style={{ width: '100%' }}
                        defaultValue={[
                          this.props.startDate
                            ? dayjs(this.props.startDate)
                            : undefined,
                          this.props.endDate
                            ? dayjs(this.props.endDate)
                            : undefined,
                        ]}
                        presets={[
                          {
                            label: 'Today',
                            value: [dayjs(), dayjs()],
                          },
                          {
                            label: 'This Month',
                            value: [
                              dayjs().startOf('month'),
                              dayjs().endOf('month'),
                            ],
                          },
                          {
                            label: 'Last Month',
                            value: [
                              dayjs().subtract(1, 'month').startOf('month'),
                              dayjs().subtract(1, 'month').endOf('month'),
                            ],
                          },
                          {
                            label: 'Year to Date',
                            value: [dayjs().startOf('year'), dayjs()],
                          },
                          {
                            label: 'Last Year',
                            value: [
                              dayjs().subtract(1, 'year').startOf('year'),
                              dayjs().subtract(1, 'year').endOf('year'),
                            ],
                          },
                        ]}
                        onChange={this.onChange.bind(this)}
                        disabledDate={disabledDate}
                        onCalendarChange={(dateArray) => {
                          const endDateSelected = dateArray[1] !== null;
                          if (endDateSelected) {
                            return this.setState({
                              date: undefined,
                            });
                          }

                          const startDateSelected = dateArray[0];
                          if (startDateSelected) {
                            this.setState({
                              date: dateArray[0],
                            });
                          }
                        }}
                      />
                    </Spin>
                  </Col>

                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 6 }}
                    md={{ span: 6 }}
                    lg={{ span: 4 }}
                  >
                    {this.state.dataCSVToBeExported.length > 0 && (
                      <CSVLink
                        data={this.state.dataCSVToBeExported}
                        filename={`RichReturns-Trend-${this.props.tab}-${this.state.timestamp}.csv`}
                        className="hidden"
                        ref={this.csvLinkRef}
                      />
                    )}

                    <Dropdown
                      overlay={
                        <Menu>
                          <Menu.Item
                            key="csv"
                            onClick={this.handleExportCsv.bind(this)}
                            disabled={this.state.exportLoading}
                          >
                            Export CSV
                          </Menu.Item>
                          <Menu.Item
                            key="pdf"
                            onClick={this.handleExportPdf}
                            disabled={this.state.pdfLoading}
                          >
                            Export PDF
                          </Menu.Item>
                        </Menu>
                      }
                      placement="bottomRight"
                    >
                      <Button block={true}>Export</Button>
                    </Dropdown>
                  </Col>
                </Row>

                {/* <Divider style={{ marginTop: 30, marginBottom: 30 }}>
                        Select a start and end date
                      </Divider> */}
                <div ref={this.printRef}>
                  <Row
                    type="flex"
                    justify="center"
                    align="middle"
                    style={{ paddingTop: 20 }}
                  >
                    <Col span={24}>
                      <div style={{ overflowX: 'auto' }}>
                        <div style={{ minWidth: 768 }}>
                          <Area
                            data={this.state.data}
                            {...this.props.config}
                            {...config}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </React.Fragment>
            ) : (
              <Row type="flex" justify="space-around" align="top">
                <Col span={24}>
                  <Divider />
                </Col>
                <Col span={24}>
                  <Upgrade
                    title="Business Intelligence at your fingertips."
                    description="Choose a plan with access to Reports. Explore insights into why customers return products and make smarter business decisions."
                    videoId="VjbdXeknVMM"
                  />
                </Col>
              </Row>
            )}
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }
}

export default VolumeGraph;
